import { Button, Form, Input } from '@bancobarigui/barihaus';
import { Table, Space, Modal, Switch, Select, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import { ProtectedRouteContainer } from '@/components/ProtectedRouteContainer';
import { PageHeader } from '@/components/PageHeader';
import { ServiceCheck, FeatureAlert } from '@/shared/components';
import { StatusBadge } from '@/components/StatusBadge';
import { useInspectionIssueTypeParamList } from './useInspectionIssueTypeParamList';
import type { InspectionIssueTypeParam } from '@/services/inspectionIssueTypeParam.service';

const SOURCE_ROLE_LABELS: Record<string, string> = {
  Inspector: 'Inspetor',
  Provider: 'Prestadora',
  Bank: 'Banco',
  All: 'Todos',
};

const TARGET_ROLE_LABELS: Record<string, string> = {
  Provider: 'Prestadora',
  Bank: 'Banco',
  All: 'Todos',
};

const SOURCE_ROLE_COLORS: Record<string, string> = {
  Inspector: 'cyan',
  Provider: 'purple',
  Bank: 'geekblue',
  All: 'default',
};

const TARGET_ROLE_COLORS: Record<string, string> = {
  Provider: 'purple',
  Bank: 'geekblue',
  All: 'default',
};

export function InspectionIssueTypeParamList() {
  const {
    issueTypes,
    loading,
    isModalOpen,
    editingItem,
    submitting,
    form,
    handleNew,
    handleEdit,
    handleDelete,
    handleModalCancel,
    handleModalSubmit,
  } = useInspectionIssueTypeParamList();

  const tableColumns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: InspectionIssueTypeParam, b: InspectionIssueTypeParam) =>
        a.name.localeCompare(b.name),
    },
    {
      title: 'Descrição',
      dataIndex: 'description',
      key: 'description',
      render: (description: string | null) => description || '-',
    },
    {
      title: 'Quem Cria',
      dataIndex: 'sourceRole',
      key: 'sourceRole',
      render: (role: string) => (
        <Tag color={SOURCE_ROLE_COLORS[role] || 'default'}>
          {SOURCE_ROLE_LABELS[role] || role}
        </Tag>
      ),
    },
    {
      title: 'Para Quem',
      dataIndex: 'targetRole',
      key: 'targetRole',
      render: (role: string) => (
        <Tag color={TARGET_ROLE_COLORS[role] || 'default'}>
          {TARGET_ROLE_LABELS[role] || role}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean) => (
        <StatusBadge
          label={isActive ? 'Ativo' : 'Inativo'}
          color={isActive ? '#22c55e' : '#ef4444'}
        />
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 200,
      render: (_: unknown, record: InspectionIssueTypeParam) => (
        <Space size="small">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Editar
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Excluir
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <ProtectedRouteContainer
      pageHeader={
        <PageHeader
          title="Tipos de Pendência"
          actions={
            <Button buttonColor="primary" icon={<PlusOutlined />} onClick={handleNew}>
              Novo Tipo de Pendência
            </Button>
          }
        />
      }
    >
      <FeatureAlert services={['inspection']} />
      <ServiceCheck services={['inspection']} featureName="Tipos de Pendência">
        <div className="flex flex-col gap-4">
          <Table
            columns={tableColumns}
            dataSource={issueTypes}
            loading={loading}
            rowKey="id"
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total de ${total} tipos`,
            }}
            scroll={{ x: 1000 }}
          />

          <Modal
            title={editingItem ? 'Editar Tipo de Pendência' : 'Novo Tipo de Pendência'}
            open={isModalOpen}
            onCancel={handleModalCancel}
            onOk={handleModalSubmit}
            confirmLoading={submitting}
            okText={editingItem ? 'Salvar' : 'Criar'}
            cancelText="Cancelar"
          >
            <Form form={form} layout="vertical" className="mt-4">
              <Form.Item
                name="name"
                label="Nome"
                rules={[{ required: true, message: 'Por favor, informe o nome' }]}
              >
                <Input placeholder="Nome do tipo de pendência" />
              </Form.Item>

              <Form.Item name="description" label="Descrição">
                <Input placeholder="Descrição (opcional)" />
              </Form.Item>

              <Form.Item
                name="sourceRole"
                label="Quem Cria (Origem)"
                rules={[{ required: true, message: 'Por favor, selecione quem cria' }]}
              >
                <Select
                  placeholder="Selecione o papel que cria este tipo"
                  options={[
                    { value: 'Inspector', label: 'Inspetor' },
                    { value: 'Provider', label: 'Prestadora' },
                    { value: 'Bank', label: 'Banco' },
                    { value: 'All', label: 'Todos' },
                  ]}
                />
              </Form.Item>

              <Form.Item
                name="targetRole"
                label="Para Quem (Destino)"
                rules={[{ required: true, message: 'Por favor, selecione para quem' }]}
              >
                <Select
                  placeholder="Selecione o papel destinatário"
                  options={[
                    { value: 'Provider', label: 'Prestadora' },
                    { value: 'Bank', label: 'Banco' },
                    { value: 'All', label: 'Todos' },
                  ]}
                />
              </Form.Item>

              {editingItem && (
                <Form.Item name="isActive" label="Ativo" valuePropName="checked" initialValue={true}>
                  <Switch />
                </Form.Item>
              )}
            </Form>
          </Modal>
        </div>
      </ServiceCheck>
    </ProtectedRouteContainer>
  );
}
